import type { ResetPasswordDetails } from '@/scopes/auth/domain/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from '@/intl'
import { useAuthActions, useAuthRecaptcha } from '@/scopes/auth/infrastructure/dependencies'
import { useAuthStore } from '@/scopes/auth/infrastructure/useAuthStore'
import { getIsSubmitDisabled } from '@/scopes/auth/pages/selectors/authSelectors'
import { EMAIL_INPUT_PATTERN } from '@/scopes/common/components/FormElements/patterns'
import { TextInput } from '@/scopes/common/components/FormElements/TextInput'
import { AuthErrorBanner } from '../AuthErrorBanner'
import { AuthFormLayout } from '../AuthFormLayout'
import { ResetPasswordConfirmation } from './ResetPasswordConfirmation'
import styles from './ResetPasswordForm.module.css'

export function ResetPasswordForm() {
  const intl = useIntl()
  const methods = useForm<ResetPasswordDetails>({ defaultValues: { email: '' } })
  const { resetPasswordRequested, loginFormOpened } = useAuthActions()
  const submitStatus = useAuthStore(state => state.submitStatus)
  const hasSubmitError = useAuthStore(state => state.submitError !== null)
  const isSubmitDisabled = useAuthStore(getIsSubmitDisabled)
  const recaptchaRef = useAuthRecaptcha()

  async function handleSubmit(details: ResetPasswordDetails) {
    const recaptchaToken = await recaptchaRef.current?.execute()

    return resetPasswordRequested({ ...details, recaptchaToken })
  }

  const isLoadingStatus = submitStatus === 'loading'

  if (submitStatus === 'success') {
    return (
      <ResetPasswordConfirmation
        email={methods.getValues('email')}
        onBackToSignIn={loginFormOpened}
      />
    )
  }

  return (
    <FormProvider {...methods}>
      <AuthFormLayout
        onSubmit={methods.handleSubmit(handleSubmit)}
        title={intl.formatMessage({ id: 'auth_reset_password_form_title' })}
        subtitle={intl.formatMessage({ id: 'auth_reset_password_form_subtitle' })}
        dataQa="auth-reset-password-form"
      >
        <div className={styles.fields}>
          {hasSubmitError && <AuthErrorBanner message={intl.formatMessage({ id: 'auth_reset_password_form_error_generic' })} />}
          <TextInput
            name="email"
            label={intl.formatMessage({ id: 'auth_form_email_label' })}
            required
            requiredErrorMessage={intl.formatMessage({ id: 'auth_form_email_required_error' })}
            pattern={EMAIL_INPUT_PATTERN}
            patternErrorMessage={intl.formatMessage({ id: 'auth_form_email_pattern_error' })}
            disabled={isLoadingStatus}
          />
        </div>
        <div className={styles.footer}>
          <Button type="submit" variant="primary" fullWidth isLoading={isLoadingStatus} disabled={isSubmitDisabled}>
            {intl.formatMessage({ id: 'auth_reset_password_form_submit_label' })}
          </Button>
          <Typography as="button" type="button" variant="body-s-regular-tight" className={styles.link} onClick={loginFormOpened} disabled={isLoadingStatus}>
            {intl.formatMessage({ id: 'auth_reset_password_form_back_to_sign_in_label' })}
          </Typography>
        </div>
      </AuthFormLayout>
    </FormProvider>
  )
}
